import { useState, useMemo, useCallback, useEffect } from 'react';
import { useFormacao } from '@/context/FormacaoContext';
import { useFuncionariosFormacao } from '@/context/FuncionariosFormacaoContext';
import { supabase } from '@/lib/supabase';
import { MUNICIPIO_FORMACAO_ID } from '@/lib/municipios';

interface Formador {
  id: string;
  funcionario_id: string | null;
  nome: string;
  email: string | null;
  telefone: string | null;
  areas_formacao: string[];
  certificacoes: string | null;
  ativo: boolean;
  observacoes: string | null;
  created_at: string;
}

interface FormadorForm {
  funcionario_id: string;
  nome: string;
  email: string;
  telefone: string;
  areas_formacao: string;
  certificacoes: string;
  observacoes: string;
  ativo: boolean;
}

const AREAS_SUGERIDAS = [
  'Segurança e Saúde no Trabalho',
  'Informática e Tecnologia',
  'Gestão e Liderança',
  'Jurídica e Administrativa',
  'Comunicação e Atendimento',
  'Ambiente e Sustentabilidade',
  'Recursos Humanos',
  'Financeira e Contabilidade',
  'Urbanismo e Obras',
  'Serviços Sociais',
];

const AVATAR_PALETTE = [
  { bg: 'bg-rose-100', text: 'text-rose-600' },
  { bg: 'bg-amber-100', text: 'text-amber-600' },
  { bg: 'bg-teal-100', text: 'text-teal-600' },
  { bg: 'bg-cyan-100', text: 'text-cyan-600' },
  { bg: 'bg-orange-100', text: 'text-orange-600' },
  { bg: 'bg-emerald-100', text: 'text-emerald-600' },
];

function getAvatar(nome: string) {
  const idx = nome.charCodeAt(0) % AVATAR_PALETTE.length;
  return AVATAR_PALETTE[idx];
}

function getInitials(nome: string) {
  return nome.split(' ').filter((_, i, arr) => i === 0 || i === arr.length - 1).map((n) => n[0] ?? '').join('').toUpperCase();
}

export default function FormadoresInternosPage() {
  const { formacoes, participantes } = useFormacao();
  const { funcionarios } = useFuncionariosFormacao();

  const [formadores, setFormadores] = useState<Formador[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState<Formador | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const [form, setForm] = useState<FormadorForm>({
    funcionario_id: '',
    nome: '',
    email: '',
    telefone: '',
    areas_formacao: '',
    certificacoes: '',
    observacoes: '',
    ativo: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const loadFormadores = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('formacao_formadores')
      .select('*')
      .eq('municipio_id', MUNICIPIO_FORMACAO_ID)
      .order('nome');
    setFormadores((data as Formador[]) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { loadFormadores(); }, [loadFormadores]);

  // ── Estatísticas por formador ─────────────────────────────────────────────
  const statsFormadores = useMemo(() => {
    return formadores.map((f) => {
      // Formações onde este formador foi o formador interno
      const formacoesMinistradas = formacoes.filter((fc) => (fc as { formador_interno_id?: string }).formador_interno_id === f.id);
      const totalHoras = formacoesMinistradas.reduce((s, fc) => s + (fc.duracao_horas ?? 0), 0);
      const totalParticipantes = formacoesMinistradas.reduce((s, fc) => {
        return s + participantes.filter((p) => p.formacao_id === fc.id).length;
      }, 0);
      const custoEvitado = formacoesMinistradas.reduce((s, fc) => s + (fc.custo ?? 0), 0);
      return { ...f, formacoesMinistradas: formacoesMinistradas.length, totalHoras, totalParticipantes, custoEvitado };
    });
  }, [formadores, formacoes, participantes]);

  const filtered = useMemo(() => {
    return statsFormadores.filter((f) => {
      const matchSearch = search === '' ||
        f.nome.toLowerCase().includes(search.toLowerCase()) ||
        (f.email ?? '').toLowerCase().includes(search.toLowerCase());
      const matchArea = areaFilter === '' || f.areas_formacao.includes(areaFilter);
      return matchSearch && matchArea;
    });
  }, [statsFormadores, search, areaFilter]);

  const todasAreas = useMemo(() => {
    const set = new Set<string>();
    formadores.forEach((f) => f.areas_formacao.forEach((a) => set.add(a)));
    return Array.from(set).sort();
  }, [formadores]);

  const kpis = useMemo(() => {
    const ativos = formadores.filter((f) => f.ativo).length;
    const totalHoras = statsFormadores.reduce((s, f) => s + f.totalHoras, 0);
    const totalFormacoes = statsFormadores.reduce((s, f) => s + f.formacoesMinistradas, 0);
    return { ativos, totalHoras, totalFormacoes };
  }, [formadores, statsFormadores]);

  function openCreate() {
    setEditTarget(null);
    setForm({ funcionario_id: '', nome: '', email: '', telefone: '', areas_formacao: '', certificacoes: '', observacoes: '', ativo: true });
    setErrors({});
    setShowModal(true);
  }

  function openEdit(f: Formador) {
    setEditTarget(f);
    setForm({
      funcionario_id: f.funcionario_id ?? '',
      nome: f.nome,
      email: f.email ?? '',
      telefone: f.telefone ?? '',
      areas_formacao: f.areas_formacao.join(', '),
      certificacoes: f.certificacoes ?? '',
      observacoes: f.observacoes ?? '',
      ativo: f.ativo,
    });
    setErrors({});
    setShowModal(true);
  }

  // Quando seleciona funcionário, preenche nome e email automaticamente
  function handleFuncSelect(funcId: string) {
    const func = funcionarios.find((f) => f.id === funcId);
    setForm((p) => ({
      ...p,
      funcionario_id: funcId,
      nome: func?.nome ?? p.nome,
      email: func?.email ?? p.email,
    }));
  }

  async function handleSave() {
    const errs: Record<string, string> = {};
    if (!form.nome.trim()) errs.nome = 'Nome obrigatório';
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSaving(true);
    const areas = form.areas_formacao
      .split(',')
      .map((a) => a.trim())
      .filter(Boolean);

    const payload = {
      municipio_id: MUNICIPIO_FORMACAO_ID,
      funcionario_id: form.funcionario_id || null,
      nome: form.nome.trim(),
      email: form.email || null,
      telefone: form.telefone || null,
      areas_formacao: areas,
      certificacoes: form.certificacoes || null,
      observacoes: form.observacoes || null,
      ativo: form.ativo,
      updated_at: new Date().toISOString(),
    };

    if (editTarget) {
      await supabase.from('formacao_formadores').update(payload).eq('id', editTarget.id);
    } else {
      await supabase.from('formacao_formadores').insert(payload);
    }
    await loadFormadores();
    setShowModal(false);
    setSaving(false);
    setSuccessMsg(editTarget ? 'Formador atualizado!' : 'Formador registado!');
    setTimeout(() => setSuccessMsg(''), 3000);
  }

  async function handleDelete(id: string) {
    await supabase.from('formacao_formadores').delete().eq('id', id);
    setFormadores((prev) => prev.filter((f) => f.id !== id));
    setConfirmDelete(null);
  }

  return (
    <>
      <main className="flex-1 bg-slate-50/60 overflow-auto">
        {/* Header */}
        <div className="bg-white border-b border-zinc-100 px-8 py-5">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-zinc-900 flex-shrink-0">
                <i className="ri-user-voice-line text-white text-lg" />
              </div>
              <div>
                <span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest">Formação</span>
                <h1 className="text-[20px] font-bold text-zinc-900 tracking-tight leading-tight">Formadores Internos</h1>
                <p className="text-[12px] text-zinc-400 mt-0.5">Funcionários que ministram formação interna · Redução de custos</p>
              </div>
            </div>
            <button
              onClick={openCreate}
              className="whitespace-nowrap flex items-center gap-2 bg-zinc-900 text-white text-[13px] font-medium px-4 py-2.5 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-user-add-line text-sm" /></div>
              Novo Formador
            </button>
          </div>
        </div>

        <div className="px-8 py-6 space-y-5">
          {successMsg && (
            <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 text-emerald-700 text-[13px] px-4 py-3 rounded-xl">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0"><i className="ri-checkbox-circle-fill text-sm" /></div>
              {successMsg}
            </div>
          )}

          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: 'ri-user-voice-line', value: formadores.length, label: 'Formadores Registados', bg: 'bg-zinc-100 text-zinc-600' },
              { icon: 'ri-check-double-line', value: kpis.ativos, label: 'Formadores Ativos', bg: 'bg-emerald-50 text-emerald-600' },
              { icon: 'ri-book-open-line', value: kpis.totalFormacoes, label: 'Formações Ministradas', bg: 'bg-zinc-100 text-zinc-600' },
              { icon: 'ri-time-line', value: `${kpis.totalHoras}h`, label: 'Horas de Formação Interna', bg: 'bg-amber-50 text-amber-600' },
            ].map((s) => (
              <div key={s.label} className="bg-white rounded-xl border border-zinc-100 p-4 flex items-center gap-3">
                <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${s.bg}`}>
                  <i className={`${s.icon} text-base`} />
                </div>
                <div>
                  <p className="text-[20px] font-bold text-zinc-900 leading-tight">{s.value}</p>
                  <p className="text-[11px] font-medium text-zinc-500">{s.label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Vantagem da formação interna */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-4 flex items-start gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-100 flex-shrink-0 mt-0.5">
              <i className="ri-leaf-line text-emerald-600 text-sm" />
            </div>
            <div>
              <p className="text-[13px] font-bold text-emerald-800">Formação Interna — Vantagens</p>
              <p className="text-[12px] text-emerald-700 mt-0.5">
                A formação ministrada por formadores internos reduz custos, valoriza o conhecimento interno e conta para as metas legais de formação.
                Registe os formadores internos e associe-os às ações de formação para acompanhar o impacto.
              </p>
            </div>
          </div>

          {/* Filtros */}
          <div className="bg-white rounded-xl border border-zinc-100 p-4 flex flex-wrap gap-2.5 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <div className="w-8 h-8 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none">
                <i className="ri-search-line text-sm" />
              </div>
              <input
                type="text"
                placeholder="Pesquisar formador..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-700 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-300"
              />
            </div>
            {todasAreas.length > 0 && (
              <select
                value={areaFilter}
                onChange={(e) => setAreaFilter(e.target.value)}
                className="text-[13px] border border-zinc-200 rounded-lg px-3 py-2.5 bg-white text-zinc-600 focus:outline-none cursor-pointer max-w-[220px]"
              >
                <option value="">Todas as áreas</option>
                {todasAreas.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            )}
            <span className="text-[12px] text-zinc-400 ml-auto">
              <span className="font-semibold text-zinc-600">{filtered.length}</span> formador(es)
            </span>
          </div>

          {/* Conteúdo */}
          {loading ? (
            <div className="bg-white rounded-xl border border-zinc-100 py-20 flex flex-col items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-loader-4-line text-3xl animate-spin text-zinc-300" />
              </div>
              <p className="text-[13px] text-zinc-400">A carregar formadores...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="bg-white rounded-xl border border-zinc-100 py-20 flex flex-col items-center gap-3">
              <div className="w-12 h-12 flex items-center justify-center">
                <i className="ri-user-voice-line text-4xl text-zinc-200" />
              </div>
              <p className="text-[14px] font-semibold text-zinc-400">
                {formadores.length === 0 ? 'Nenhum formador interno registado' : 'Nenhum formador encontrado'}
              </p>
              {formadores.length === 0 && (
                <button
                  onClick={openCreate}
                  className="whitespace-nowrap flex items-center gap-2 mt-2 bg-zinc-900 text-white text-[13px] font-medium px-4 py-2.5 rounded-lg hover:bg-zinc-800 cursor-pointer"
                >
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-user-add-line text-sm" /></div>
                  Registar primeiro formador
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              {filtered.map((f) => {
                const palette = getAvatar(f.nome);
                return (
                  <div key={f.id} className={`bg-white rounded-xl border overflow-hidden transition-all hover:border-zinc-200 ${!f.ativo ? 'opacity-60' : 'border-zinc-100'}`}>
                    <div className="p-5">
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-11 h-11 flex items-center justify-center rounded-full text-[13px] font-bold flex-shrink-0 ${palette.bg} ${palette.text}`}>
                            {getInitials(f.nome)}
                          </div>
                          <div>
                            <p className="text-[13px] font-bold text-zinc-900 leading-tight">{f.nome}</p>
                            {f.email && <p className="text-[11px] text-zinc-400 mt-0.5 truncate max-w-[160px]">{f.email}</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${f.ativo ? 'bg-emerald-50 text-emerald-700' : 'bg-zinc-100 text-zinc-500'}`}>
                            {f.ativo ? 'Ativo' : 'Inativo'}
                          </span>
                        </div>
                      </div>

                      {/* Áreas */}
                      {f.areas_formacao.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {f.areas_formacao.slice(0, 3).map((area) => (
                            <span key={area} className="text-[10px] font-medium text-zinc-600 bg-zinc-100 px-2 py-0.5 rounded-full">{area}</span>
                          ))}
                          {f.areas_formacao.length > 3 && (
                            <span className="text-[10px] text-zinc-400 bg-zinc-50 px-2 py-0.5 rounded-full">+{f.areas_formacao.length - 3}</span>
                          )}
                        </div>
                      )}

                      {/* Stats */}
                      <div className="grid grid-cols-3 gap-2 mb-3">
                        {[
                          { label: 'Formações', value: f.formacoesMinistradas },
                          { label: 'Horas', value: `${f.totalHoras}h` },
                          { label: 'Participantes', value: f.totalParticipantes },
                        ].map((s) => (
                          <div key={s.label} className="bg-zinc-50 rounded-lg p-2 text-center">
                            <p className="text-[15px] font-black text-zinc-900">{s.value}</p>
                            <p className="text-[9px] text-zinc-400 font-medium">{s.label}</p>
                          </div>
                        ))}
                      </div>

                      {f.certificacoes && (
                        <p className="text-[11px] text-zinc-500 mb-3">
                          <span className="font-semibold">Certificações:</span> {f.certificacoes}
                        </p>
                      )}

                      {/* Ações */}
                      <div className="flex items-center gap-2 pt-3 border-t border-zinc-100">
                        <button
                          onClick={() => openEdit(f)}
                          className="whitespace-nowrap flex-1 flex items-center justify-center gap-1.5 text-[12px] font-semibold text-zinc-600 bg-zinc-100 hover:bg-zinc-200 px-3 py-2 rounded-lg transition-colors cursor-pointer"
                        >
                          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-edit-line text-xs" /></div>
                          Editar
                        </button>
                        {confirmDelete === f.id ? (
                          <div className="flex items-center gap-1">
                            <button onClick={() => handleDelete(f.id)} className="whitespace-nowrap text-[11px] font-semibold text-white bg-rose-500 px-2.5 py-2 rounded-lg cursor-pointer">Sim</button>
                            <button onClick={() => setConfirmDelete(null)} className="whitespace-nowrap text-[11px] text-zinc-400 bg-zinc-100 px-2.5 py-2 rounded-lg cursor-pointer">Não</button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setConfirmDelete(f.id)}
                            className="whitespace-nowrap w-9 h-9 flex items-center justify-center rounded-lg text-zinc-300 hover:text-rose-500 hover:bg-rose-50 transition-colors cursor-pointer"
                          >
                            <i className="ri-delete-bin-line text-sm" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Modal criar/editar */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}
        >
          <div className="bg-white rounded-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-7 pt-6 pb-4 border-b border-zinc-100 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-zinc-900">
                  <i className="ri-user-voice-line text-white text-base" />
                </div>
                <div>
                  <h2 className="text-[16px] font-bold text-zinc-900">{editTarget ? 'Editar Formador' : 'Novo Formador Interno'}</h2>
                  <p className="text-[11px] text-zinc-400">Funcionário que ministra formação interna</p>
                </div>
              </div>
              <button onClick={() => setShowModal(false)} className="whitespace-nowrap w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 cursor-pointer">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="px-7 py-5 space-y-4">
              {/* Ligar a funcionário existente */}
              <div>
                <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">Funcionário da Câmara <span className="text-zinc-400 font-normal">(opcional)</span></label>
                <select
                  value={form.funcionario_id}
                  onChange={(e) => handleFuncSelect(e.target.value)}
                  className="w-full px-3.5 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-400 cursor-pointer"
                >
                  <option value="">Formador externo / não listado</option>
                  {funcionarios.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
                </select>
                <p className="text-[10px] text-zinc-400 mt-1">Seleciona para preencher automaticamente nome e email.</p>
              </div>

              {/* Nome */}
              <div>
                <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">Nome <span className="text-rose-500">*</span></label>
                <input
                  type="text"
                  value={form.nome}
                  onChange={(e) => setForm((p) => ({ ...p, nome: e.target.value }))}
                  placeholder="Nome completo do formador"
                  className={`w-full px-3.5 py-2.5 text-[13px] border rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-400 ${errors.nome ? 'border-rose-400' : 'border-zinc-200'}`}
                />
                {errors.nome && <p className="text-[11px] text-rose-500 mt-1">{errors.nome}</p>}
              </div>

              {/* Email + Telefone */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                    placeholder="email@camara.pt"
                    className="w-full px-3.5 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-400"
                  />
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">Telefone</label>
                  <input
                    type="tel"
                    value={form.telefone}
                    onChange={(e) => setForm((p) => ({ ...p, telefone: e.target.value }))}
                    placeholder="9XX XXX XXX"
                    className="w-full px-3.5 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-400"
                  />
                </div>
              </div>

              {/* Áreas de formação */}
              <div>
                <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">Áreas de Formação</label>
                <input
                  type="text"
                  value={form.areas_formacao}
                  onChange={(e) => setForm((p) => ({ ...p, areas_formacao: e.target.value }))}
                  placeholder="Ex: Segurança, Informática, Gestão (separadas por vírgula)"
                  className="w-full px-3.5 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-400"
                />
                <div className="flex flex-wrap gap-1 mt-2">
                  {AREAS_SUGERIDAS.slice(0, 6).map((area) => (
                    <button
                      key={area}
                      type="button"
                      onClick={() => {
                        const atual = form.areas_formacao.split(',').map((a) => a.trim()).filter(Boolean);
                        if (!atual.includes(area)) {
                          setForm((p) => ({ ...p, areas_formacao: [...atual, area].join(', ') }));
                        }
                      }}
                      className="whitespace-nowrap text-[10px] font-medium text-zinc-500 bg-zinc-100 hover:bg-zinc-200 px-2 py-0.5 rounded-full cursor-pointer transition-colors"
                    >
                      + {area}
                    </button>
                  ))}
                </div>
              </div>

              {/* Certificações */}
              <div>
                <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">Certificações / Habilitações</label>
                <input
                  type="text"
                  value={form.certificacoes}
                  onChange={(e) => setForm((p) => ({ ...p, certificacoes: e.target.value }))}
                  placeholder="Ex: CAP Formador, Licenciatura em Engenharia..."
                  className="w-full px-3.5 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-400"
                />
              </div>

              {/* Observações */}
              <div>
                <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">Observações</label>
                <textarea
                  value={form.observacoes}
                  onChange={(e) => setForm((p) => ({ ...p, observacoes: e.target.value }))}
                  rows={2}
                  placeholder="Notas adicionais sobre o formador..."
                  className="w-full px-3.5 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-400 resize-none"
                  maxLength={500}
                />
              </div>

              {/* Ativo */}
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setForm((p) => ({ ...p, ativo: !p.ativo }))}
                  className={`whitespace-nowrap flex items-center gap-2 px-4 py-2 rounded-lg border-2 text-[13px] font-semibold transition-all cursor-pointer ${form.ativo ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-zinc-200 bg-white text-zinc-500'}`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${form.ativo ? 'ri-checkbox-circle-fill' : 'ri-checkbox-blank-circle-line'} text-sm`} />
                  </div>
                  {form.ativo ? 'Formador Ativo' : 'Formador Inativo'}
                </button>
              </div>

              <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-zinc-100">
                <button onClick={() => setShowModal(false)} className="whitespace-nowrap px-5 py-2.5 text-[13px] font-medium text-zinc-600 bg-zinc-100 rounded-lg hover:bg-zinc-200 cursor-pointer">
                  Cancelar
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="whitespace-nowrap flex items-center gap-2 px-5 py-2.5 text-[13px] font-semibold text-white bg-zinc-900 rounded-lg hover:bg-zinc-800 cursor-pointer disabled:opacity-50"
                >
                  {saving ? <><div className="w-4 h-4 flex items-center justify-center"><i className="ri-loader-4-line text-sm animate-spin" /></div>A guardar...</> : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
